import type { CharacterProfile, ChatExportFields } from "@shared/schema";
import type { APIKeyConfig } from "@/types/api-key";
import type { ProviderResponse } from "@/types/usage";
import { parseOpenAIUsage } from "./usage-helpers";
import { parseJsonLike } from './json-utils';
import { providerRegistry } from './provider-registry';

export const DEFAULT_CHUTES_BASE_URL = 'https://llm.chutes.ai/v1';

export function normalizeChutesEndpoint(endpoint: string): string {
  const raw = (endpoint || '').trim();
  if (!raw) {
    throw new Error(`Chutes base URL is required (e.g., ${DEFAULT_CHUTES_BASE_URL})`);
  }

  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  let url: URL;
  try {
    url = new URL(withProtocol);
  } catch {
    throw new Error('Invalid Chutes base URL');
  }

  // Strip query/hash to avoid leaking secrets or params
  url.search = '';
  url.hash = '';

  let path = url.pathname || '';
  // Remove trailing slashes for normalization
  path = path.replace(/\/+$/, '');

  // Allow users to paste a full endpoint like /v1/chat/completions or /v1/models by trimming the route segment
  path = path.replace(/\/chat\/completions$/i, '');
  path = path.replace(/\/models$/i, '');

  // Re-trim trailing slashes after removal
  path = path.replace(/\/+$/, '');

  if (path === '' || path === '/') {
    path = '/v1';
  } else if (!path.endsWith('/v1')) {
    path = `${path}/v1`;
  }

  url.pathname = path;

  const normalized = url.toString();
  return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
}

function isChutesModel(model: string): boolean {
  const chutesConfig = providerRegistry.get('chutes');
  if (!chutesConfig?.models?.text) {
    return false;
  }
  return chutesConfig.models.text.includes(model);
}

function resolveChutesBaseUrl(endpoint?: string): string {
  try {
    return normalizeChutesEndpoint(endpoint || "");
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Invalid Chutes base URL';
    throw new Error(message);
  }
}

function extractJsonObject(text: string): string {
  let trimmed = text.trim();
  const codeMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  if (codeMatch) {
    trimmed = codeMatch[1].trim();
  }
  const jsonMatch = trimmed.match(/(\{[\s\S]*\})/);
  if (jsonMatch) {
    trimmed = jsonMatch[1];
  }
  return trimmed;
}

function parseJsonSafe<T>(text: string): T {
  const cleaned = extractJsonObject(text);
  return JSON.parse(cleaned) as T;
}

async function postChatCompletions(
  baseUrl: string,
  apiKey: string,
  payload: any,
  opts?: { signal?: AbortSignal }
): Promise<any> {
  const resp = await fetch(`${baseUrl}/chat/completions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(payload),
    signal: opts?.signal,
  });

  if (!resp.ok) {
    let message = `HTTP ${resp.status}`;
    try {
      const err = await resp.json();
      message = err?.error?.message || err?.message || message;
    } catch {}
    throw new Error(message);
  }

  return resp.json();
}

export async function validateChutesKey(config: { apiKey: string; model: string; customEndpoint?: string }): Promise<{ isValid: boolean; error?: string }>{
  const apiKey = config.apiKey?.trim();
  if (!apiKey) {
    return { isValid: false, error: "API key is required" };
  }

  if (!isChutesModel(config.model)) {
    return { isValid: false, error: `Unsupported model: ${config.model}` };
  }

  try {
    resolveChutesBaseUrl(config.customEndpoint);
  } catch (error) {
    return { isValid: false, error: error instanceof Error ? error.message : 'Invalid Chutes base URL' };
  }

  // Chutes does not expose a models endpoint; rely on static catalog validation and user-provided chute URL.
  return { isValid: true };
}

export async function generateCharacterProfileChutes(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<CharacterProfile>> {
  const baseUrl = resolveChutesBaseUrl(api.customEndpoint);
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: "system", content: prompts.system },
      { role: "user", content: prompts.user },
    ],
  };
  const json = await postChatCompletions(baseUrl, api.apiKey, payload, opts);
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? "").trim();
  const parsed = parseJsonSafe<any>(content);
  const data = parsed?.character_profile || parsed?.character || parsed;
  return { value: data as CharacterProfile, usage };
}

export async function generateChatExportFieldsChutes(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<ChatExportFields>> {
  const baseUrl = resolveChutesBaseUrl(api.customEndpoint);
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: "system", content: prompts.system },
      { role: "user", content: prompts.user },
    ],
  };
  const json = await postChatCompletions(baseUrl, api.apiKey, payload, opts);
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? "").trim();
  return { value: parseJsonSafe<ChatExportFields>(content), usage };
}

export async function generateImagePlanPromptsChutes(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<{ naturalPrompt: string; stableDiffusionPrompt: string; negativePrompt?: string }>> {
  const baseUrl = resolveChutesBaseUrl(api.customEndpoint);
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: "system", content: prompts.system },
      { role: "user", content: prompts.user },
    ],
  };
  const json = await postChatCompletions(baseUrl, api.apiKey, payload, opts);
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? "").trim();
  let value: { naturalPrompt: string; stableDiffusionPrompt: string; negativePrompt?: string };
  try {
    value = parseJsonLike<typeof value>(content);
  } catch {
    const out: any = {};
    const lines = content.split(/\r?\n/);
    const keys: Array<'naturalPrompt' | 'stableDiffusionPrompt' | 'negativePrompt'> = ['naturalPrompt', 'stableDiffusionPrompt', 'negativePrompt'];
    for (const key of keys) {
      for (const raw of lines) {
        const line = raw.trim();
        const re = new RegExp(`(^|[\\n\\-*•\\s])"?${key}"?\\s*[:=]\\s*(.+)$`, 'i');
        const m = line.match(re);
        if (m && m[2]) {
          let v = String(m[2]).trim();
          v = v.replace(/[`,]$/, '').replace(/^"|^'|^`/, '').replace(/"$|'$|`$/, '').trim();
          if (v) { out[key] = v; break; }
        }
      }
    }
    if (out.naturalPrompt && !out.stableDiffusionPrompt) out.stableDiffusionPrompt = out.naturalPrompt;
    if (!out.naturalPrompt && out.stableDiffusionPrompt) out.naturalPrompt = out.stableDiffusionPrompt;
    if (!out.naturalPrompt || !out.stableDiffusionPrompt) {
      throw new Error('Provider returned non-JSON without extractable prompts');
    }
    value = out;
  }
  return { value, usage };
}

export async function rewriteCharacterFieldChutes(
  instructions: string,
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<string>> {
  const baseUrl = resolveChutesBaseUrl(api.customEndpoint);
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: "user", content: instructions },
    ],
  };
  const json = await postChatCompletions(baseUrl, api.apiKey, payload, opts);
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? "").trim();
  const codeBlock = content.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  const value = (codeBlock ? codeBlock[1] : content).trim();
  return { value, usage };
}

export async function generateRoleplayReplyChutes(
  prompts: { system: string; messages: Array<{ role: "user" | "assistant"; content: string }> },
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<string>> {
  const baseUrl = resolveChutesBaseUrl(api.customEndpoint);
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: "system", content: prompts.system },
      ...prompts.messages.map((m) => ({ role: m.role, content: m.content })),
    ],
  };
  const json = await postChatCompletions(baseUrl, api.apiKey, payload, opts);
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? "").trim();
  return { value: content, usage };
}

export async function editCharacterChutes(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<any>> {
  const baseUrl = resolveChutesBaseUrl(api.customEndpoint);
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: "system", content: prompts.system },
      { role: "user", content: prompts.user },
    ],
  };
  const json = await postChatCompletions(baseUrl, api.apiKey, payload, opts);
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? "").trim();
  const parsed = parseJsonSafe<any>(content);
  return { value: parsed, usage };
}
